import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copier } from "../../../../../../components/copier";
import CaretRight from "../../../../../assets/svg/icons/common/CaretRight";
import CheckGreenCircleIcon from "../../../../../assets/svg/icons/common/check-green-circle";
import CloseIcon24 from "../../../../../assets/svg/icons/common/CloseIcon24";
import CopyIcon from "../../../../../assets/svg/icons/common/copy";
import MinusCircle from "../../../../../assets/svg/icons/common/MinusCircle";
import ButtonIcon from "../../../../../components/ButtonIcon/ButtonIcon";
import MonthPicker from "../../../../../components/form/month-picker";
import { Select } from "../../../../../components/form/select";
import HeadingWithLine from "../../../../../components/HeadingWithLine/HeadingWithLine";
import MobileDesktopAlert from "../../../../../components/MobileDesktopAlert";
import Pagination from "../../../../../components/Pagination/Pagination";
import Table from "../../../../../components/Table";
import { useMediaQuery } from "../../../../../hooks/useMediaQuery";
import useCommonStore from "../../../../../stores/common";
import useResidualStore from "../../../../../stores/residuals";
import PaymentAuthorizationReportSkeleton from "../components/PaymentAuthorizationReportSkeleton";
import {
  formatDisplayDate,
  formatMoney,
  monthAbbrevAndYearToCalendarMonth,
} from "../formatters";
import styles from "../styles.module.scss";
import ledger from "./ledgerTable.module.scss";

const DOCUMENT_TYPE_LABELS = {
  1: "Payment",
  2: "Invoice",
  3: "Credit Memo",
  4: "Fin. Chrg. Memo",
  5: "Reminder",
  6: "Refund",
};

const ENTRY_TYPE_LABELS = {
  1: "Initial",
  2: "Application",
};

const documentTypeLabel = (v) =>
  DOCUMENT_TYPE_LABELS[v] ?? (v != null ? String(v) : "—");

const entryTypeLabel = (v) =>
  ENTRY_TYPE_LABELS[v] ?? (v != null ? String(v) : "—");

const cell = (value) => (value != null && value !== "" ? String(value) : "—");

const documentDisplayDate = (row) =>
  formatDisplayDate(row.posting_date ?? row.transaction_date);

const MobileStatementFields = ({ row }) => (
  <>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Type</span>
      <div>
        <span>{documentTypeLabel(row.document_type)}</span>
      </div>
    </div>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Document No.</span>
      <div>
        <span>{cell(row.document_number)}</span>
      </div>
    </div>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Document date</span>
      <div>
        <span>{documentDisplayDate(row)}</span>
      </div>
    </div>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Description</span>
      <div>
        <span>{cell(row.cle_description)}</span>
      </div>
    </div>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Entry type</span>
      <div>
        <span>{entryTypeLabel(row.entry_type)}</span>
      </div>
    </div>
    <div className={styles.dataItem}>
      <span className={styles.lineItemFieldLabel}>Amount</span>
      <div>
        <span
          className={
            Number(row.document_type) === 1 ? ledger.mainAmountCellPayment : ""
          }
        >
          {formatMoney(row.amount)}
        </span>
      </div>
    </div>
  </>
);

const InvoiceStatement = () => {
  const isInvoiceNarrowLayout = useMediaQuery(1024);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const setExpandDetails = useCommonStore((state) => state.setExpandDetails);
  const officeOptions = useResidualStore((s) => s.paymentAuthOfficeOptions);
  const selectedOffice = useResidualStore((s) => s.paymentAuthSelectedOffice);
  const officesLoading = useResidualStore((s) => s.paymentAuthOfficesLoading);
  const setPaymentAuthSelectedOffice = useResidualStore(
    (s) => s.setPaymentAuthSelectedOffice,
  );
  const setAgentStatementAppliedPeriod = useResidualStore(
    (s) => s.setAgentStatementAppliedPeriod,
  );
  const reportData = useResidualStore((s) => s.agentStatementReportData);
  const reportLoading = useResidualStore((s) => s.agentStatementReportLoading);
  const page = useResidualStore((s) => s.agentStatementPage);
  const perPage = useResidualStore((s) => s.agentStatementPerPage);
  const setPage = useResidualStore((s) => s.setAgentStatementPage);
  const pagination = reportData?.pagination;
  const [expandedRowIds, setExpandedRowIds] = useState(() => new Set());

  const lineGroups = useMemo(
    () =>
      Array.isArray(reportData?.line_groups) ? reportData.line_groups : [],
    [reportData?.line_groups],
  );

  const tableRows = useMemo(
    () =>
      lineGroups.map((g, idx) => ({
        ...(g.parent ?? {}),
        id: String(g.parent?.entry_number ?? `row-${idx}`),
        children: Array.isArray(g.children) ? g.children : [],
      })),
    [lineGroups],
  );

  useEffect(() => {
    setExpandedRowIds(new Set());
  }, [
    reportData?.statement_period?.end,
    selectedOffice,
    lineGroups.length,
  ]);

  const toggleRow = useCallback((id) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const renderExpandedContent = useCallback((row) => {
    const details = row.children;
    if (!details?.length) {
      return null;
    }
    const last = details.length - 1;
    return (
      <div className={ledger.nestedContainer}>
        <table className={ledger.nestedTable}>
          <thead>
            <tr className={ledger.nestedHeaderRow}>
              <td />
              <td>DOC TYPE</td>
              <td>DOC NO.</td>
              <td>DOC DATE</td>
              <td>DESCRIPTION</td>
              <td>ENTRY TYPE</td>
              <td>AMOUNT</td>
            </tr>
          </thead>
          <tbody>
            {details.map((child, index) => {
              return (
                <tr
                  key={child.entry_number ?? index}
                  className={`${ledger.nestedRow} ${ledger.nestedChildApp} ${index !== last ? ledger.nestedLine : ""}`}
                >
                  <td>
                    <div className={ledger.normalCell}>
                      {documentTypeLabel(child.document_type)}
                    </div>
                  </td>
                  <td
                    className={ledger.nestedReferenceCell}
                    title={cell(child.document_number)}
                  >
                    {cell(child.document_number)}
                  </td>
                  <td>{documentDisplayDate(child)}</td>
                  <td
                    title={[
                      cell(child.name),
                      cell(child.customer_num),
                      cell(child.cle_description),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  >
                    {cell(child.cle_description)}
                  </td>
                  <td>
                    <span>
                      <div className={ledger.nestedTxnType}>
                        {entryTypeLabel(child.entry_type)}
                      </div>
                    </span>
                  </td>
                  <td
                    className={ledger.nestedAmountCell}
                    title={formatMoney(child.amount)}
                  >
                    {formatMoney(child.amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "expand",
        width: "16px",
        headerName: "",
        renderCell: (_, row) => {
          const has = (row.children?.length ?? 0) > 0;
          if (!has) {
            return null;
          }
          const isOpen = expandedRowIds.has(row.id);
          return (
            <button
              type="button"
              className={`${ledger.expandButton} ${isOpen ? ledger.expandCaretOpen : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(row.id);
              }}
              aria-expanded={isOpen}
              aria-label={
                isOpen ? "Collapse applications" : "Expand applications"
              }
            >
              <CaretRight />
            </button>
          );
        },
      },
      {
        field: "document_type",
        headerName: "DOC TYPE",
        width: "100px",
        renderCell: (_, row) => (
          <div className={ledger.normalCell}>
            {documentTypeLabel(row.document_type)}
          </div>
        ),
      },
      {
        field: "document_number",
        headerName: "DOC NO.",
        width: "100px",
        overflow: true,
        renderCell: (_, row) => (
          <div className={ledger.normalCell}>{cell(row.document_number)}</div>
        ),
      },
      {
        field: "document_date",
        headerName: "DOC DATE",
        width: "100px",
        renderCell: (_, row) => (
          <div className={ledger.normalCell}>{documentDisplayDate(row)}</div>
        ),
      },
      {
        field: "cle_description",
        headerName: "DESCRIPTION",
        width: "200px",
        overflow: true,
        renderCell: (_, row) => (
          <div className={ledger.normalCell} title={cell(row.cle_description)}>
            {cell(row.cle_description)}
          </div>
        ),
      },
      {
        field: "entry_type",
        headerName: "ENTRY TYPE",
        width: "100px",
        renderCell: (_, row) => (
          <div className={ledger.normalCell}>
            {entryTypeLabel(row.entry_type)}
          </div>
        ),
      },
      {
        field: "amount",
        headerName: "AMOUNT",
        width: "120px",
        renderCell: (_, row) => (
          <div
            className={`${ledger.mainAmountCell}${Number(row.document_type) === 1 ? ` ${ledger.mainAmountCellPayment}` : ""}`}
          >
            {formatMoney(row.amount)}
          </div>
        ),
      },
    ],
    [expandedRowIds, toggleRow],
  );

  const onCloseClick = () => {
    setShowDetails(false);
    setExpandDetails(false);
  };

  const onMonthSubmit = ({ year, month: monthAbbrev }) => {
    const next = monthAbbrevAndYearToCalendarMonth(monthAbbrev, year);
    if (next) {
      setAgentStatementAppliedPeriod(next);
    }
  };

  return (
    <div className={`${styles.container} ${styles.invoiceStatementRoot}`}>
      <header className={styles.header}>
        <div className={styles.closeContainer}>
          <ButtonIcon onClick={onCloseClick}>
            <CloseIcon24 />
          </ButtonIcon>
        </div>

        <div className={styles.titleContainer}>
          <h2 className={styles.title}>Invoice statement</h2>

          <div className={styles.buttonsContainer}>
            <MonthPicker
              variant="smallFilter"
              onSubmit={onMonthSubmit}
              changeOnSubmit
              showActions
              defaultDate={new Date()}
            />

            <div className={styles.officeSelectWrap}>
              <Select
                hideClearButton
                name="office"
                placeholder={officesLoading ? "Loading" : "Select office"}
                options={officeOptions}
                value={selectedOffice}
                onChange={setPaymentAuthSelectedOffice}
                disabled={officesLoading}
                secondary
                customClass={styles.officeSelect}
              />
            </div>
          </div>
        </div>
      </header>
      <main>
        {reportLoading && <PaymentAuthorizationReportSkeleton />}
        {!reportLoading && selectedOffice && (
          <>
            {reportData ? (
              <>
                <HeadingWithLine text="Statement summary" />

                <div className={styles.dataContainer}>
                  <div className={`${styles.dataItem} ${styles.dataItemSpan2}`}>
                    <span>Statement for</span>
                    <div>
                      <span>{reportData.statement_for ?? "—"}</span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Statement period</span>
                    <div>
                      <span>
                        {formatDisplayDate(reportData.statement_period?.start)}{" "}
                        — {formatDisplayDate(reportData.statement_period?.end)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.dataItem}>
                    <span>Office</span>
                    <div>
                      <span>{selectedOffice ?? "—"}</span>
                      {selectedOffice ? (
                        <Copier text={String(selectedOffice)}>
                          <CopyIcon />
                        </Copier>
                      ) : null}
                    </div>
                  </div>
                </div>

                {lineGroups.length > 0 ? (
                  <div className={styles.lineItemsSection}>
                    <HeadingWithLine text="Ledger lines" />
                    {pagination && pagination.last_page > 1 ? (
                      <div className={styles.paginationContainer}>
                        <Pagination
                          pageCount={pagination.last_page}
                          currentPage={Math.max(0, page - 1)}
                          totalItems={pagination.total}
                          itemsPerPage={perPage}
                          onPageChange={({ selected }) =>
                            setPage(selected + 1)
                          }
                        />
                      </div>
                    ) : null}

                    {!isInvoiceNarrowLayout ? (
                      <div className={ledger.tableContainer}>
                        <Table
                          key={`ledger-${selectedOffice}-${lineGroups.length}`}
                          columns={columns}
                          data={tableRows.map((row) => ({
                            ...row,
                            expanded: expandedRowIds.has(row.id),
                          }))}
                          renderExpandedContent={renderExpandedContent}
                          expandedContentClassName={ledger.ledgerExpandedContentNoPad}
                          dataLoading={false}
                          sortBy={null}
                          sortColumn={null}
                          className={styles.agentStatementLedgerTable}
                          disablePaddingOnExpandedRow
                          noMarginTop
                        />
                      </div>
                    ) : (
                      <div className={styles.agentStatementMobileLedger}>
                        {tableRows.map((row, groupIndex) => {
                          const { id, children = [], ...parent } = row;
                          const childList = children;
                          const hasChildren = childList.length > 0;
                          const isOpen = expandedRowIds.has(id);

                          return (
                            <div
                              key={`${id}-${groupIndex}`}
                              className={styles.agentStatementMobileGroup}
                            >
                              <div
                                className={`${styles.dataContainer} ${styles.dataContainerLine} ${styles.lineItemRow}`}
                              >
                                <MobileStatementFields row={parent} />
                              </div>
                              {hasChildren ? (
                                <div
                                  className={
                                    styles.agentStatementMobileExpandRow
                                  }
                                >
                                  <button
                                    type="button"
                                    className={`${ledger.expandButton} ${styles.agentStatementMobileExpandBtn} ${isOpen ? ledger.expandCaretOpen : ""}`}
                                    onClick={() => toggleRow(id)}
                                    aria-expanded={isOpen}
                                  >
                                    <CaretRight />
                                  </button>
                                </div>
                              ) : null}
                              {hasChildren && isOpen
                                ? childList.map((child, ci) => (
                                    <React.Fragment
                                      key={child.entry_number ?? ci}
                                    >
                                      <div
                                        className={`${styles.dataContainer} ${styles.dataContainerLine} ${styles.lineItemRow} ${styles.agentStatementMobileChildCard}`}
                                      >
                                        <MobileStatementFields row={child} />
                                      </div>
                                      {ci < childList.length - 1 ? (
                                        <hr
                                          className={styles.lineItemDivider}
                                        />
                                      ) : null}
                                    </React.Fragment>
                                  ))
                                : null}
                              <hr className={styles.lineItemDivider} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={styles.reportStatus}>
                    No agent account statement lines for this office and period.
                  </p>
                )}
              </>
            ) : (
              <p className={styles.reportStatus}>
                Unable to load the report. Try another office or period.
              </p>
            )}
          </>
        )}
      </main>

      <MobileDesktopAlert message="See more details on the desktop version" />
    </div>
  );
};

export default InvoiceStatement;
